/**
 * Feedback History Panel
 * Issue #365: View and manage feedback history for AI naming
 *
 * Shows all feedback given on AI-generated names, including:
 * - Approved names (good/edited)
 * - Rejected names (bad)
 * - Usage count as examples
 */

import { useState, useCallback, useEffect } from 'react';
import { apiFetch } from '../../services/api';

interface FeedbackHistoryPanelProps {
  projectId: string;
  onClose: () => void;
}

interface FeedbackEntry {
  id: string;
  interactionId: string;
  handlerName: string | null;
  triggerElement: string | null;
  componentFile: string | null;
  aiSuggestedName: string;
  aiConfidence: number | null;
  feedbackType: 'good' | 'bad' | 'edited';
  userProvidedName: string | null;
  createdAt: string;
  usedAsExampleCount: number;
}

type SortField = 'createdAt' | 'usedAsExampleCount' | 'feedbackType';
type SortDirection = 'asc' | 'desc';
type FilterType = 'all' | 'good' | 'bad' | 'edited';

export function FeedbackHistoryPanel({ projectId, onClose }: FeedbackHistoryPanelProps) {
  const [feedback, setFeedback] = useState<FeedbackEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Load feedback history
  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        const response = await apiFetch(`/projects/${projectId}/name-feedback`, {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('Failed to load feedback history');
        }

        const data = await response.json();
        setFeedback(data.feedback || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load feedback');
      } finally {
        setIsLoading(false);
      }
    };

    fetchFeedback();
  }, [projectId]);

  // Delete a feedback entry
  const handleDelete = useCallback(async (feedbackId: string) => {
    if (!confirm('Delete this feedback entry? It will no longer be used as an example.')) {
      return;
    }

    setDeletingId(feedbackId);
    try {
      const response = await apiFetch(`/projects/${projectId}/name-feedback/${feedbackId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to delete feedback');
      }

      setFeedback(prev => prev.filter(f => f.id !== feedbackId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    } finally {
      setDeletingId(null);
    }
  }, [projectId]);

  // Clear all feedback
  const handleClearAll = useCallback(async () => {
    if (!confirm('Clear all feedback history? This will remove all learned examples.')) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiFetch(`/projects/${projectId}/name-feedback`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to clear feedback');
      }

      setFeedback([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear');
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  // Sort and filter feedback
  const sortedFeedback = [...feedback]
    .filter(f => filterType === 'all' || f.feedbackType === filterType)
    .sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'createdAt':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        case 'usedAsExampleCount':
          comparison = a.usedAsExampleCount - b.usedAsExampleCount;
          break;
        case 'feedbackType':
          comparison = a.feedbackType.localeCompare(b.feedbackType);
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });

  // Stats
  const stats = {
    total: feedback.length,
    good: feedback.filter(f => f.feedbackType === 'good').length,
    edited: feedback.filter(f => f.feedbackType === 'edited').length,
    bad: feedback.filter(f => f.feedbackType === 'bad').length,
    totalUsed: feedback.reduce((sum, f) => sum + f.usedAsExampleCount, 0),
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <span className="text-2xl">📊</span>
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
              Feedback History
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Stats Bar */}
        <div className="px-4 py-3 bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-6 text-sm">
            <span className="text-slate-600 dark:text-slate-400">
              <strong className="text-slate-800 dark:text-slate-200">{stats.total}</strong> entries
            </span>
            <span className="text-green-600 dark:text-green-400">
              <strong>{stats.good}</strong> approved
            </span>
            <span className="text-blue-600 dark:text-blue-400">
              <strong>{stats.edited}</strong> edited
            </span>
            <span className="text-red-600 dark:text-red-400">
              <strong>{stats.bad}</strong> rejected
            </span>
            <span className="text-indigo-600 dark:text-indigo-400 ml-auto">
              <strong>{stats.totalUsed}</strong> times used as examples
            </span>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500 dark:text-slate-400">Filter:</span>
            {(['all', 'good', 'edited', 'bad'] as FilterType[]).map(type => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`px-2 py-1 text-xs font-medium rounded ${
                  filterType === type
                    ? 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                }`}
              >
                {type === 'all' ? 'All' : type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500 dark:text-slate-400">Sort:</span>
            <select
              value={`${sortField}-${sortDirection}`}
              onChange={(e) => {
                const [field, dir] = e.target.value.split('-') as [SortField, SortDirection];
                setSortField(field);
                setSortDirection(dir);
              }}
              className="text-xs px-2 py-1 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300"
            >
              <option value="createdAt-desc">Newest first</option>
              <option value="createdAt-asc">Oldest first</option>
              <option value="usedAsExampleCount-desc">Most used</option>
              <option value="usedAsExampleCount-asc">Least used</option>
              <option value="feedbackType-asc">By type</option>
            </select>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
            </div>
          ) : sortedFeedback.length === 0 ? (
            <div className="text-center py-12 text-slate-500 dark:text-slate-400">
              <p className="text-lg mb-2">No feedback yet</p>
              <p className="text-sm">
                Approve or edit AI-generated names to build your learning dataset.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {sortedFeedback.map(entry => (
                <FeedbackEntryRow
                  key={entry.id}
                  entry={entry}
                  onDelete={handleDelete}
                  isDeleting={deletingId === entry.id}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-slate-200 dark:border-slate-700">
          <button
            onClick={handleClearAll}
            disabled={feedback.length === 0 || isLoading}
            className="px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Clear All Feedback
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// Individual feedback entry row
function FeedbackEntryRow({
  entry,
  onDelete,
  isDeleting,
}: {
  entry: FeedbackEntry;
  onDelete: (id: string) => void;
  isDeleting: boolean;
}) {
  const typeColors = {
    good: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800',
    edited: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800',
    bad: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800',
  };

  const typeLabels = {
    good: 'Approved',
    edited: 'Edited',
    bad: 'Rejected',
  };

  const finalName = entry.userProvidedName || entry.aiSuggestedName;

  return (
    <div className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-900/30 rounded-lg border border-slate-200 dark:border-slate-700">
      {/* Type Badge */}
      <span className={`px-2 py-0.5 text-xs font-medium rounded border ${typeColors[entry.feedbackType]}`}>
        {typeLabels[entry.feedbackType]}
      </span>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-mono text-sm text-slate-600 dark:text-slate-400 truncate">
            {entry.handlerName || 'Unknown handler'}
          </span>
          <span className="text-slate-400">&rarr;</span>
          <span className="font-medium text-slate-800 dark:text-slate-200 truncate">
            "{finalName}"
          </span>
        </div>
        <div className="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400">
          {entry.componentFile && (
            <span className="truncate max-w-[200px]" title={entry.componentFile}>
              {entry.componentFile.split('/').pop()}
            </span>
          )}
          <span>
            {new Date(entry.createdAt).toLocaleDateString()}
          </span>
          {entry.usedAsExampleCount > 0 && (
            <span className="text-indigo-600 dark:text-indigo-400">
              Used {entry.usedAsExampleCount}x as example
            </span>
          )}
          {entry.aiConfidence !== null && (
            <span>
              AI confidence: {Math.round(entry.aiConfidence * 100)}%
            </span>
          )}
        </div>
        {entry.feedbackType === 'edited' && entry.aiSuggestedName !== entry.userProvidedName && (
          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            <span className="line-through">{entry.aiSuggestedName}</span>
            <span className="mx-1">&rarr;</span>
            <span className="text-blue-600 dark:text-blue-400">{entry.userProvidedName}</span>
          </div>
        )}
      </div>

      {/* Delete button */}
      <button
        onClick={() => onDelete(entry.id)}
        disabled={isDeleting}
        className="p-1 text-slate-400 hover:text-red-500 dark:hover:text-red-400 disabled:opacity-50"
        title="Delete this feedback"
      >
        {isDeleting ? (
          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        )}
      </button>
    </div>
  );
}

export default FeedbackHistoryPanel;
